import {Reducer, updateFactory} from '@modifi/redux'
import {aUserFeedBackSubmitSuccessful} from '@modifi/plugin-onboarding'
import {aPush} from '@modifi/router'
import {getAuthenticatedThemisClient} from '@modifi/onboarding-api-clients'
import {isEmpty, pick} from 'ramda'
import {aCloseDialog} from '@modifi/plugin-dialogs'

import {getLogger} from '../../../../../utils'
import {ROUTE_INTRO} from '../../../../../routes/constants'
import {
  aFetchSellerTradePartnerDocumentsByTradePartnerId,
  aFetchSellerTradePartners,
  selectSellerTradePartnerDocumentsByTradePartnerId,
} from '../../../../../data/seller-trade-partners'
import {
  PRE_UNDERWRITING_BUYER_DETAILS,
  SECTION_PRE_UNDERWRITING_BUYER_DETAILS,
} from '../../constants'
import {aHideBuyerForm} from '../../buyer-details/store/view'
import {aFetchInputFields} from '../../../../../data/input-fields'

const logger = getLogger('trade-documents-upload-page')

const apiClient = getAuthenticatedThemisClient()

export const selectInitialFormData = (
  state: Record<string, any>,
  tradePartnerId?: string
): {initialValues: Record<string, any>; defaultValues: Record<string, any>} => {
  const tradeDocuments = tradePartnerId
    ? selectSellerTradePartnerDocumentsByTradePartnerId(state, tradePartnerId)
    : undefined

  const initialValues: Record<string, any> = {
    sampleDocuments: [],
  }

  if (tradeDocuments && tradeDocuments?.length > 0) {
    Object.assign(
      initialValues,
      tradeDocuments.reduce(
        (result, document) => {
          result.sampleDocuments.push(pick(['documentType', 'fileId'], document))
          return result
        },
        {sampleDocuments: []} as {
          sampleDocuments: any[]
        }
      )
    )
  }

  return {
    initialValues,
    defaultValues: {},
  }
}

const {actions, selectors, reducer} = updateFactory({
  actionNamePrefix: 'pages/trade-documents-upload-form/submit',
  rootSelector: state => state?.pages?.preUnderwriting?.tradeDocumentsForm,
  submitRequest:
    (a, changes: Record<string, any>, externalTradePartnerId: string) =>
    async (dispatch): Promise<void> => {
      logger.info('Uploading docs for buyer in preUnderwriting=%s', externalTradePartnerId)
      logger.debug('changes: %o', changes)

      dispatch(a.pendingAction())

      try {
        if (isEmpty(changes)) {
          logger.info('No document related changed detected - skipping')
        } else {
          logger.info('Linking & Unlinking documents...')

          await Promise.all([
            ...(changes.sampleDocuments?.added || changes.sampleDocuments).map((file: any) =>
              apiClient.TradePartners.attachDocument({
                externalBuyerTradePartnerId: externalTradePartnerId,
                body: file,
              })
            ),
            ...(changes.sampleDocuments?.removed || []).map((fileId: any) =>
              apiClient.TradePartners.detachDocument({
                externalBuyerTradePartnerId: externalTradePartnerId,
                fileId,
              })
            ),
          ])

          await dispatch(aFetchSellerTradePartnerDocumentsByTradePartnerId(externalTradePartnerId))
        }
      } catch (e: any) {
        logger.error(`Error occurred: %o`, e)
        dispatch(a.errorAction(e))
        return
      }

      dispatch(a.successAction())

      dispatch(aCloseDialog())
      await dispatch(aFetchSellerTradePartners())
      await dispatch(aFetchInputFields())
      dispatch(aPush(ROUTE_INTRO, {page: PRE_UNDERWRITING_BUYER_DETAILS}))
      dispatch(aHideBuyerForm())
      dispatch(aUserFeedBackSubmitSuccessful(SECTION_PRE_UNDERWRITING_BUYER_DETAILS))
    },
})

export const {aSubmit: aSavePage} = actions

export default reducer as Reducer

export const {
  selectUpdatePending: selectSavePending,

  selectUpdateErrorMessage: selectSaveErrorMessage,
} = selectors
